<template>
    <ContentComp>
        <FormLayout
            v-if="model != null"
            class="gap-5"
            :label-width="250"
        >
            <FormLayoutRow>
                <FormLayoutLabel
                    text="stationrole.gpiport1"
                    tooltip="stationrole.gpiporttooltip"
                ></FormLayoutLabel>
                <StationRoleGPISelector
                    v-model:option="model.GPI1Operation"
                    v-model:value1="model.GPI1Value1"
                    v-model:value2="model.GPI1Value2"
                />
            </FormLayoutRow>

            <FormLayoutRow class="mt-4">
                <FormLayoutLabel
                    text="stationrole.gpiport2"
                    tooltip="stationrole.gpiporttooltip"
                ></FormLayoutLabel>
                <StationRoleGPISelector
                    v-model:option="model.GPI2Operation"
                    v-model:value1="model.GPI2Value1"
                    v-model:value2="model.GPI2Value2"
                />
            </FormLayoutRow>

            <FormLayoutRow class="mt-4">
                <FormLayoutLabel
                    text="stationrole.gpiport3"
                    tooltip="stationrole.gpiporttooltip"
                ></FormLayoutLabel>
                <StationRoleGPISelector
                    v-model:option="model.GPI3Operation"
                    v-model:value1="model.GPI3Value1"
                    v-model:value2="model.GPI3Value2"
                />
            </FormLayoutRow>

            <FormLayoutRow class="mt-4">
                <FormLayoutLabel
                    text="stationrole.gpiport4"
                    tooltip="stationrole.gpiporttooltip"
                ></FormLayoutLabel>
                <StationRoleGPISelector
                    v-model:option="model.GPI4Operation"
                    v-model:value1="model.GPI4Value1"
                    v-model:value2="model.GPI4Value2"
                />
            </FormLayoutRow>
        </FormLayout>

        <h4>{{ $tm('stationrole.gpiportsettings') }}</h4>

        <FormLayoutRow v-if="model != null">
            <FormLayoutLabel
                text="stationrole.allowonlygpioormanual"
                tooltip="stationrole.allowonlygpioormanualtooltip"
            ></FormLayoutLabel>
            <InputBool v-model="model.AllowOnlyGPIOOrManual" />
        </FormLayoutRow>

        <div class="d-flex flex-row gap-2">
            <ButtonComp
                button-type="Add"
                @click="addStationroleOverride"
                >{{ $tm('button.add') }}</ButtonComp
            >
            <ButtonComp
                button-type="Delete"
                @click="deleteStationroleOverride"
                >{{ $tm('button.delete') }}</ButtonComp
            >
        </div>

        <GridComp
            ref="grid"
            class="eq-grid flex-grow-1"
            :gridSetup="gridSetup"
        />
    </ContentComp>
</template>
<script lang="ts" setup>
import { ButtonComp, GridComp, InputBool } from '@/components';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import ContentComp from '@/components/layout/ContentComp.vue';
import { StationRole, StationRoleOverride } from '@/objects/generated';
import StationRoleGPISelector from './StationRoleGPISelector.vue';
import { GridColumnDefinition, GridDataSource, GridSetup, type IGridCompExposed } from '@/components/Grid/Classes';
import type { IGridRowGetterParams } from '@/components/Grid/Classes/GridDataSource';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import { propertyof } from '@/utilities';
import { ref } from 'vue';
import { getDateWithMaxTime } from '@/utilities/dateutils';
import { useEventCommonStoreRef } from '@/stores';

/**
 * The vmodel is passed from the parent component
 */
const model = defineModel<StationRole | null>({ required: true });

const { Event } = useEventCommonStoreRef();

const grid = ref<IGridCompExposed<StationRoleOverride> | null>(null);

/**
 * The grid should be read only so all the columns should be editable = false
 */
const gridColumnDefinitions = [
    new GridColumnOptionsBuilder<StationRoleOverride>(
        propertyof<StationRoleOverride>('ActiveFrom'),
        'enginehandler.activefrom',
        undefined,
        'datetime',
    ).Build(),

    new GridColumnOptionsBuilder<StationRoleOverride>(
        propertyof<StationRoleOverride>('ActiveTo'),
        'enginehandler.activeto',
        undefined,
        'datetime',
    ).Build(),

    new GridColumnOptionsBuilder<StationRoleOverride>(
        propertyof<StationRoleOverride>('OnlyGpiOrManual'),
        'stationrole.allowonlygpioormanual',
        undefined,
        'boolean',
    ).Build(),
];

/**
 * Set up the datasource
 */
const dataSource = new GridDataSource<StationRoleOverride>({
    totalNumberOfRows: () => model.value?.Overrides.length ?? 0, // Maybe this needs to change for a server side data source

    rowGetter: (params: IGridRowGetterParams<StationRoleOverride>) => {
        let filtered = model.value?.Overrides.filter((x) => x.EntityState != 'Deleted').slice(params.startRow, params.endRow) ?? [];

        // Sort
        params.sort(filtered);

        params.successCallback(filtered, filtered, filtered.length);
    },
});

/**
 * Setup the grid
 */
const gridSetup = new GridSetup<StationRoleOverride>({
    rowIdGetter: (row: StationRoleOverride) => row.ID?.toString() ?? '',
    hasCheckboxSelectionColumn: true,
    rowSelection: 'multiple',
    datasource: dataSource,
    GridColumnDefinition: new GridColumnDefinition({
        CommonColumnOptions: {
            sortable: false,
            filterable: false,
            editable: true,
        },
        ColumnOptions: gridColumnDefinitions,
    }),
});

/**
 * Add a new stationrole override
 */
const addStationroleOverride = () => {
    if (model.value == null || model.value.ID == null) {
        return;
    }

    const newOverride = StationRoleOverride.FromJsValues({
        EntityState: 'Added',
        ActiveFrom: getDateWithMaxTime(Event.value?.Date ?? new Date()),
        ActiveTo: getDateWithMaxTime(Event.value?.Date ?? new Date(), true),
    });

    model.value.Overrides.push(newOverride);
    grid.value?.refreshCachedData();
};

/**
 * Will delete a stationrole override
 * If the value has an ID of > 0 it exists in the database and we mark it for delete, or else we just remove it from the list
 */
const deleteStationroleOverride = async () => {
    if (grid.value == null) {
        return;
    }
    const selectedNodes = grid.value?.getSelectedRows() ?? [];
    if (selectedNodes.length === 0) {
        return;
    }

    selectedNodes.forEach((node) => {
        if (node.EntityState === 'Added') {
            // Just remove it from the list
            model.value?.Overrides.splice(
                model.value.Overrides.findIndex((x) => x === node),
                1,
            );
        } else {
            node.Delete();
        }
    });

    grid.value?.refreshCachedData();
};
</script>
